/**
 * Validates a return path used after login to prevent open-redirect attacks.
 *
 * Accepts only same-origin paths that start with a single `/` followed by a
 * non-slash, non-backslash character. Everything else is rejected:
 * - absolute URLs (e.g. "http://evil.com")
 * - protocol-relative URLs (e.g. "//evil.com")
 * - paths with backslashes after the leading slash (e.g. "/\evil.com")
 * - scheme-prefixed values (e.g. "javascript:alert(1)", "data:text/html,...")
 * - bare root "/" — falls through to the caller's default so the root redirect
 *   chain runs once instead of being repeated after login
 * - empty/undefined/non-string values
 *
 * Colons are permitted *inside* the path (query strings, ISO timestamps,
 * resource IDs). Scheme detection is anchored to the start of the string —
 * an embedded `javascript:` in a query string is not a navigation target by
 * itself; consumers that re-dispatch query data must sanitize on their own.
 *
 * Returns the path unchanged when valid, or null when it should be ignored.
 */
export function sanitizeReturnPath(path: string | undefined | null): string | null {
  if (!path || typeof path !== 'string') return null
  // Must start with "/" followed by a safe char. Blocks "//evil", "/\evil", and bare "/".
  if (!/^\/[^/\\]/.test(path)) return null
  // Reject scheme-prefixed values ("javascript:...", "data:...", "vbscript:...").
  // RFC 3986 scheme shape, anchored to start.
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return null
  return path
}
